import PptxGenJS from 'pptxgenjs';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PptxSignalEntity {
  name: string;
  entity_id?: string;
  entity_type?: string;
  value?: number;
  owner?: string;
  status?: string;
  last_activity_type?: string;
  last_activity_date?: string;
  days_in_current_state?: number;
  metric_value?: number;
}

interface PptxAccountabilityItem {
  entity: string;
  owner: string;
  issue: string;
}

interface PptxPrescriptiveAction {
  who: string;
  what: string;
  when: string;
}

interface PptxBusinessImpactStructured {
  revenue_at_risk?: string;
  overdue_cash?: string;
  operational_delays?: string;
  narrative?: string;
}

interface PptxForecastData {
  '7_day'?: string;
  '14_day'?: string;
  '30_day'?: string;
}

interface PptxSignalEvidence {
  signal_type: string;
  signal_subtype?: string;
  source: string;
  severity: string;
  category: string;
  description: string;
  confidence: number;
  affected_entities: PptxSignalEntity[];
  summary_metrics: Record<string, unknown>;
}

interface PptxScoreBreakdown {
  base_score: number;
  category_penalties_capped: Record<string, number>;
  total_capped_penalty: number;
  cross_system_penalty: number;
  recovery_buffer: number;
  impacted_categories: string[];
  final_score: number;
}

export interface BriefPptxData {
  title: string;
  created_at: string;
  health_score: number | null;
  confidence: number;
  detected_signals: string[];
  business_impact: string;
  recommended_actions: string[];
  risk_assessment: string;
  momentum?: {
    classification: string;
    delta: number;
    label: string;
  } | null;
  userName?: string;
  companyLogoUrl?: string;
  signal_evidence?: PptxSignalEvidence[];
  root_cause_analysis?: string[];
  cross_system_correlation?: string;
  business_impact_structured?: PptxBusinessImpactStructured;
  forecast?: PptxForecastData;
  accountability?: PptxAccountabilityItem[];
  score_breakdown?: PptxScoreBreakdown | null;
}

// ---------------------------------------------------------------------------
// Brand constants
// ---------------------------------------------------------------------------

const COLORS = {
  primary: '0EA5E9',
  dark: '0F172A',
  darkAlt: '1E293B',
  text: '334155',
  textLight: '64748B',
  white: 'FFFFFF',
  green: '22C55E',
  amber: 'F59E0B',
  orange: 'F97316',
  red: 'EF4444',
  bgLight: 'F8FAFC',
  bgCard: 'F1F5F9',
  divider: 'E2E8F0',
};

const SLIDE_W = 13.33;
const SLIDE_H = 7.5;
const MARGIN = 0.75;
const CONTENT_W = SLIDE_W - MARGIN * 2;

function getHealthColor(score: number | null): string {
  if (score === null) return COLORS.textLight;
  if (score >= 90) return COLORS.green;
  if (score >= 70) return COLORS.green;
  if (score >= 50) return COLORS.amber;
  if (score >= 30) return COLORS.orange;
  return COLORS.red;
}

function getHealthLabel(score: number | null): string {
  if (score === null) return 'Unknown';
  if (score >= 90) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 50) return 'Moderate';
  if (score >= 30) return 'At Risk';
  if (score >= 10) return 'Critical';
  return 'System Failure';
}

function getMomentumLabel(classification: string): string {
  const labels: Record<string, string> = {
    'strong_improvement': 'Strong Improvement',
    'improving': 'Improving',
    'stable': 'Stable',
    'declining': 'Declining',
    'critical_decline': 'Critical Decline',
  };
  return labels[classification] || classification;
}

function getSeverityColor(severity: string): string {
  if (severity === 'critical' || severity === 'high') return COLORS.red;
  if (severity === 'medium') return COLORS.amber;
  return COLORS.green;
}

// ---------------------------------------------------------------------------
// Slide-level helpers
// ---------------------------------------------------------------------------

function addSlideFooter(slide: PptxGenJS.Slide, dateStr: string) {
  slide.addShape('rect' as PptxGenJS.ShapeType, {
    x: 0, y: SLIDE_H - 0.45, w: SLIDE_W, h: 0.45,
    fill: { color: COLORS.bgLight },
  });
  slide.addText('Generated by Core314  |  Operational Intelligence Platform', {
    x: MARGIN, y: SLIDE_H - 0.4, w: 7, h: 0.3,
    fontSize: 9, color: COLORS.textLight, fontFace: 'Arial',
  });
  slide.addText(dateStr, {
    x: SLIDE_W - MARGIN - 3, y: SLIDE_H - 0.4, w: 3, h: 0.3,
    fontSize: 9, color: COLORS.textLight, align: 'right', fontFace: 'Arial',
  });
}

function addContentHeader(slide: PptxGenJS.Slide, title: string, logoUrl?: string) {
  slide.addShape('rect' as PptxGenJS.ShapeType, {
    x: 0, y: 0, w: SLIDE_W, h: 0.85,
    fill: { color: COLORS.dark },
  });
  slide.addShape('rect' as PptxGenJS.ShapeType, {
    x: 0, y: 0.85, w: SLIDE_W, h: 0.04,
    fill: { color: COLORS.primary },
  });
  slide.addText(title, {
    x: MARGIN, y: 0.15, w: 8, h: 0.55,
    fontSize: 24, bold: true, color: COLORS.white, fontFace: 'Arial',
  });
  if (logoUrl) {
    try {
      slide.addImage({
        path: logoUrl, x: SLIDE_W - MARGIN - 2, y: 0.12, h: 0.6, w: 1.8,
        sizing: { type: 'contain', w: 1.8, h: 0.6 },
      });
    } catch {
      // skip if logo fetch fails
    }
  }
}

function addSectionTitle(slide: PptxGenJS.Slide, title: string, y: number) {
  slide.addShape('rect' as PptxGenJS.ShapeType, {
    x: MARGIN, y, w: 0.08, h: 0.32,
    fill: { color: COLORS.primary },
  });
  slide.addText(title, {
    x: MARGIN + 0.2, y, w: 8, h: 0.35,
    fontSize: 14, bold: true, color: COLORS.dark, fontFace: 'Arial',
  });
}

// ---------------------------------------------------------------------------
// Main export function
// ---------------------------------------------------------------------------

export async function generateBriefPptx(data: BriefPptxData): Promise<void> {
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE';
  pptx.author = 'Core314';
  pptx.subject = 'Operational Intelligence Brief';
  pptx.title = data.title;

  const dateStr = new Date(data.created_at).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
  const timeStr = new Date(data.created_at).toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit',
  });

  // ==========================================================================
  // SLIDE 1: Title Slide
  // ==========================================================================
  const titleSlide = pptx.addSlide();
  titleSlide.background = { color: COLORS.dark };

  titleSlide.addShape('rect' as PptxGenJS.ShapeType, {
    x: 0, y: 0, w: SLIDE_W, h: 0.06,
    fill: { color: COLORS.primary },
  });

  if (data.companyLogoUrl) {
    try {
      titleSlide.addImage({
        path: data.companyLogoUrl,
        x: MARGIN, y: 0.6, h: 1.2, w: 3.5,
        sizing: { type: 'contain', w: 3.5, h: 1.2 },
      });
    } catch {
      // Skip logo if fetch fails
    }
  }

  titleSlide.addText('Core314', {
    x: SLIDE_W - MARGIN - 3, y: 0.7, w: 3, h: 0.45,
    fontSize: 18, bold: true, color: COLORS.primary, align: 'right', fontFace: 'Arial',
  });
  titleSlide.addText('Operational Intelligence Platform', {
    x: SLIDE_W - MARGIN - 3, y: 1.1, w: 3, h: 0.3,
    fontSize: 11, color: COLORS.textLight, align: 'right', fontFace: 'Arial',
  });

  titleSlide.addShape('rect' as PptxGenJS.ShapeType, {
    x: MARGIN, y: 2.1, w: CONTENT_W, h: 0.02,
    fill: { color: COLORS.darkAlt },
  });

  titleSlide.addText('Operational Intelligence Brief', {
    x: MARGIN, y: 2.5, w: CONTENT_W, h: 0.75,
    fontSize: 36, bold: true, color: COLORS.white, fontFace: 'Arial',
  });

  titleSlide.addText(data.title, {
    x: MARGIN, y: 3.35, w: 10, h: 0.55,
    fontSize: 20, color: COLORS.primary, fontFace: 'Arial',
  });

  const bulletChar = '\u2022';
  const dashChar = '\u2014';

  titleSlide.addText(`${dateStr}  ${bulletChar}  ${timeStr}`, {
    x: MARGIN, y: 4.2, w: 6, h: 0.35,
    fontSize: 14, color: COLORS.textLight, fontFace: 'Arial',
  });

  if (data.userName) {
    titleSlide.addText(`Prepared for: ${data.userName}`, {
      x: MARGIN, y: 4.65, w: 6, h: 0.35,
      fontSize: 14, color: '94A3B8', fontFace: 'Arial',
    });
  }

  if (data.health_score !== null) {
    const healthColor = getHealthColor(data.health_score);
    const healthLabel = getHealthLabel(data.health_score);
    const cardX = MARGIN;
    const cardY = 5.3;
    const cardW = 5.5;
    const cardH = 1.4;

    titleSlide.addShape('roundRect' as PptxGenJS.ShapeType, {
      x: cardX, y: cardY, w: cardW, h: cardH,
      rectRadius: 0.12, fill: { color: COLORS.darkAlt },
    });

    titleSlide.addText(`${data.health_score}`, {
      x: cardX + 0.3, y: cardY + 0.15, w: 1.5, h: 0.85,
      fontSize: 48, bold: true, color: healthColor, align: 'center', valign: 'middle', fontFace: 'Arial',
    });
    titleSlide.addText('/ 100', {
      x: cardX + 1.8, y: cardY + 0.35, w: 0.8, h: 0.35,
      fontSize: 14, color: COLORS.textLight, fontFace: 'Arial',
    });
    titleSlide.addText(healthLabel, {
      x: cardX + 0.3, y: cardY + 0.95, w: 1.8, h: 0.3,
      fontSize: 14, bold: true, color: healthColor, align: 'center', fontFace: 'Arial',
    });

    if (data.momentum) {
      const momLabel = getMomentumLabel(data.momentum.classification);
      const deltaStr = `${data.momentum.delta >= 0 ? '+' : ''}${data.momentum.delta}`;
      const momColor = data.momentum.delta >= 0 ? COLORS.green : data.momentum.delta < -5 ? COLORS.red : COLORS.amber;
      const arrow = data.momentum.delta >= 0 ? '\u25B2' : '\u25BC';
      titleSlide.addText(`${arrow} ${momLabel} (${deltaStr})`, {
        x: cardX + 2.7, y: cardY + 0.35, w: 2.5, h: 0.35,
        fontSize: 14, bold: true, color: momColor, fontFace: 'Arial',
      });
    }
  }

  const confidenceLabel = data.confidence >= 80 ? 'High Confidence' : data.confidence >= 60 ? 'Moderate Confidence' : 'Low Confidence';
  titleSlide.addText(`CONFIDENTIAL  ${bulletChar}  ${confidenceLabel} (${data.confidence}%)`, {
    x: MARGIN, y: SLIDE_H - 0.5, w: 6, h: 0.3,
    fontSize: 10, color: COLORS.textLight, fontFace: 'Arial',
  });

  titleSlide.addShape('rect' as PptxGenJS.ShapeType, {
    x: 0, y: SLIDE_H - 0.06, w: SLIDE_W, h: 0.06,
    fill: { color: COLORS.primary },
  });

  // ==========================================================================
  // SLIDE 2: Executive Summary
  // ==========================================================================
  const summarySlide = pptx.addSlide();
  summarySlide.background = { color: COLORS.white };
  addContentHeader(summarySlide, 'Executive Summary', data.companyLogoUrl);
  addSlideFooter(summarySlide, dateStr);

  let sumY = 1.2;

  if (data.health_score !== null) {
    const healthColor = getHealthColor(data.health_score);
    const cardW = 4;
    const cardH = 1.6;

    summarySlide.addShape('roundRect' as PptxGenJS.ShapeType, {
      x: MARGIN, y: sumY, w: cardW, h: cardH,
      rectRadius: 0.1, fill: { color: COLORS.bgCard },
    });
    summarySlide.addText('HEALTH SCORE', {
      x: MARGIN + 0.25, y: sumY + 0.12, w: 3, h: 0.3,
      fontSize: 10, bold: true, color: COLORS.textLight, fontFace: 'Arial',
    });
    summarySlide.addText(`${data.health_score}`, {
      x: MARGIN + 0.25, y: sumY + 0.4, w: 1.5, h: 0.65,
      fontSize: 36, bold: true, color: healthColor, fontFace: 'Arial',
    });
    summarySlide.addText('/ 100', {
      x: MARGIN + 1.6, y: sumY + 0.55, w: 0.8, h: 0.35,
      fontSize: 14, color: COLORS.textLight, fontFace: 'Arial',
    });
    summarySlide.addText(getHealthLabel(data.health_score), {
      x: MARGIN + 0.25, y: sumY + 1.05, w: 2, h: 0.3,
      fontSize: 14, bold: true, color: healthColor, fontFace: 'Arial',
    });

    if (data.momentum) {
      const momColor = data.momentum.delta >= 0 ? COLORS.green : data.momentum.delta < -5 ? COLORS.red : COLORS.amber;
      const arrow = data.momentum.delta >= 0 ? '\u25B2' : '\u25BC';
      summarySlide.addText(`${arrow} ${getMomentumLabel(data.momentum.classification)} (${data.momentum.delta >= 0 ? '+' : ''}${data.momentum.delta})`, {
        x: MARGIN + 2.4, y: sumY + 0.55, w: 1.5, h: 0.35,
        fontSize: 11, bold: true, color: momColor, fontFace: 'Arial',
      });
    }
  }

  if (data.business_impact_structured) {
    const bis = data.business_impact_structured;
    const impactItems: { label: string; value: string; color: string }[] = [];
    if (bis.revenue_at_risk) impactItems.push({ label: 'Revenue at Risk', value: bis.revenue_at_risk, color: COLORS.red });
    if (bis.overdue_cash) impactItems.push({ label: 'Overdue Cash', value: bis.overdue_cash, color: COLORS.amber });
    if (bis.operational_delays) impactItems.push({ label: 'Op. Delays', value: bis.operational_delays, color: COLORS.orange });

    const impactCardW = 2.6;
    const startX = MARGIN + 4.3;
    impactItems.forEach((item, idx) => {
      const cx = startX + idx * (impactCardW + 0.2);
      if (cx + impactCardW <= SLIDE_W - MARGIN) {
        summarySlide.addShape('roundRect' as PptxGenJS.ShapeType, {
          x: cx, y: sumY, w: impactCardW, h: 1.6,
          rectRadius: 0.1, fill: { color: COLORS.bgCard },
        });
        summarySlide.addShape('rect' as PptxGenJS.ShapeType, {
          x: cx, y: sumY, w: impactCardW, h: 0.06,
          fill: { color: item.color },
        });
        summarySlide.addText(item.label.toUpperCase(), {
          x: cx + 0.2, y: sumY + 0.15, w: impactCardW - 0.4, h: 0.3,
          fontSize: 10, bold: true, color: COLORS.textLight, fontFace: 'Arial',
        });
        summarySlide.addText(item.value, {
          x: cx + 0.2, y: sumY + 0.5, w: impactCardW - 0.4, h: 0.5,
          fontSize: 22, bold: true, color: item.color, fontFace: 'Arial',
        });
      }
    });
  }

  sumY += 1.9;

  if (data.cross_system_correlation) {
    addSectionTitle(summarySlide, 'CROSS-SYSTEM CORRELATION', sumY);
    sumY += 0.5;
    summarySlide.addText(data.cross_system_correlation, {
      x: MARGIN + 0.2, y: sumY, w: CONTENT_W - 0.4, h: 0.8,
      fontSize: 13, color: COLORS.text, valign: 'top', fontFace: 'Arial',
    });
    sumY += 0.9;
  }

  if (data.business_impact_structured?.narrative) {
    addSectionTitle(summarySlide, 'BUSINESS IMPACT', sumY);
    sumY += 0.5;
    summarySlide.addText(data.business_impact_structured.narrative, {
      x: MARGIN + 0.2, y: sumY, w: CONTENT_W - 0.4, h: 1.0,
      fontSize: 13, color: COLORS.text, valign: 'top', fontFace: 'Arial',
    });
    sumY += 1.1;
  } else if (data.business_impact) {
    addSectionTitle(summarySlide, 'BUSINESS IMPACT', sumY);
    sumY += 0.5;
    summarySlide.addText(data.business_impact, {
      x: MARGIN + 0.2, y: sumY, w: CONTENT_W - 0.4, h: 1.0,
      fontSize: 13, color: COLORS.text, valign: 'top', fontFace: 'Arial',
    });
    sumY += 1.1;
  }

  if (data.risk_assessment) {
    addSectionTitle(summarySlide, 'RISK ASSESSMENT', sumY);
    sumY += 0.5;
    summarySlide.addText(data.risk_assessment, {
      x: MARGIN + 0.2, y: sumY, w: CONTENT_W - 0.4, h: 0.9,
      fontSize: 13, color: COLORS.text, valign: 'top', fontFace: 'Arial',
    });
  }

  // ==========================================================================
  // SLIDE 3+: Detected Signals (dynamically fit per slide)
  // ==========================================================================

  // Pre-compute card height for each signal so we can pack slides correctly
  function computeSignalCardH(ev: PptxSignalEvidence): number {
    const entCount = Math.min(ev.affected_entities.length, 5);
    const hasEnt = entCount > 0;
    const base = 1.0;              // severity badge + source + description
    const entHeader = hasEnt ? 0.35 : 0;
    const entRows = entCount * 0.45; // name line + meta line per entity
    const overflow = ev.affected_entities.length > 5 ? 0.25 : 0;
    return base + entHeader + entRows + overflow + 0.15;
  }

  if (data.signal_evidence && data.signal_evidence.length > 0) {
    const HEADER_H = 1.15;   // space used by content header
    const FOOTER_H = 0.55;   // footer clearance
    const CARD_GAP = 0.25;
    const maxContentY = SLIDE_H - FOOTER_H;

    let si = 0;
    let pageNum = 0;
    const totalPages = (() => {
      let p = 0, y = HEADER_H;
      for (let i = 0; i < data.signal_evidence.length; i++) {
        const ch = computeSignalCardH(data.signal_evidence[i]);
        if (y + ch > maxContentY && y > HEADER_H) { p++; y = HEADER_H; }
        y += ch + CARD_GAP;
      }
      return p + 1;
    })();

    while (si < data.signal_evidence.length) {
      const signalSlide = pptx.addSlide();
      signalSlide.background = { color: COLORS.white };
      pageNum++;

      const pageLabel = totalPages > 1 ? ` (${pageNum}/${totalPages})` : '';
      addContentHeader(signalSlide, `Detected Signals${pageLabel}`, data.companyLogoUrl);
      addSlideFooter(signalSlide, dateStr);

      let sigY = HEADER_H;

      while (si < data.signal_evidence.length) {
        const ev = data.signal_evidence[si];
        const cardH = computeSignalCardH(ev);

        // Start a new slide if this card won't fit (unless it's the first card)
        if (sigY + cardH > maxContentY && sigY > HEADER_H) break;

        const sevColor = getSeverityColor(ev.severity);

        signalSlide.addShape('roundRect' as PptxGenJS.ShapeType, {
          x: MARGIN, y: sigY, w: CONTENT_W, h: cardH,
          rectRadius: 0.1, fill: { color: COLORS.bgLight },
          line: { color: sevColor, width: 1.5, dashType: 'solid' },
        });

        const badgeW = 0.9;
        const badgeH = 0.3;
        signalSlide.addShape('roundRect' as PptxGenJS.ShapeType, {
          x: MARGIN + 0.2, y: sigY + 0.15, w: badgeW, h: badgeH,
          rectRadius: 0.06, fill: { color: sevColor },
        });
        signalSlide.addText(ev.severity.toUpperCase(), {
          x: MARGIN + 0.2, y: sigY + 0.15, w: badgeW, h: badgeH,
          fontSize: 10, bold: true, color: COLORS.white, align: 'center', valign: 'middle', fontFace: 'Arial',
        });

        const sourceLabel = ev.signal_subtype
          ? `${ev.source.replace(/_/g, ' ').toUpperCase()} ${dashChar} ${ev.signal_subtype}`
          : `${ev.source.replace(/_/g, ' ').toUpperCase()} ${bulletChar} ${ev.category.replace(/_/g, ' ')}`;
        signalSlide.addText(sourceLabel, {
          x: MARGIN + 1.3, y: sigY + 0.15, w: 8, h: 0.3,
          fontSize: 12, color: COLORS.textLight, fontFace: 'Arial',
        });

        signalSlide.addText(ev.description, {
          x: MARGIN + 0.2, y: sigY + 0.52, w: CONTENT_W - 0.4, h: 0.45,
          fontSize: 13, color: COLORS.text, valign: 'top', fontFace: 'Arial',
          wrap: true,
        });

        if (ev.affected_entities.length > 0) {
          const entitiesToShow = ev.affected_entities.slice(0, 5);
          let entY = sigY + 1.0;

          signalSlide.addText(`Entity Details (${ev.affected_entities.length}):`, {
            x: MARGIN + 0.2, y: entY, w: 4, h: 0.28,
            fontSize: 11, bold: true, color: COLORS.textLight, fontFace: 'Arial',
          });
          entY += 0.3;

          for (const ent of entitiesToShow) {
            const nameText = ent.value !== undefined
              ? `${ent.name} ${dashChar} $${ent.value.toLocaleString()}`
              : ent.name;
            signalSlide.addText(`${bulletChar}  ${nameText}`, {
              x: MARGIN + 0.4, y: entY, w: CONTENT_W - 0.8, h: 0.22,
              fontSize: 11, bold: true, color: COLORS.text, fontFace: 'Arial',
            });

            const metaParts: string[] = [];
            if (ent.owner) metaParts.push(ent.owner);
            if (ent.status) metaParts.push(ent.status);
            if (ent.days_in_current_state !== undefined) metaParts.push(`${ent.days_in_current_state}d in state`);

            if (metaParts.length > 0) {
              signalSlide.addText(metaParts.join('  |  '), {
                x: MARGIN + 0.6, y: entY + 0.22, w: CONTENT_W - 1.0, h: 0.2,
                fontSize: 10, color: COLORS.textLight, fontFace: 'Arial',
              });
            }
            entY += 0.45;
          }
          if (ev.affected_entities.length > 5) {
            signalSlide.addText(`+${ev.affected_entities.length - 5} more entities...`, {
              x: MARGIN + 0.4, y: entY, w: 4, h: 0.2,
              fontSize: 10, italic: true, color: COLORS.textLight, fontFace: 'Arial',
            });
          }
        }

        sigY += cardH + CARD_GAP;
        si++;
      }
    }
  } else if (data.detected_signals.length > 0) {
    const sigSlide = pptx.addSlide();
    sigSlide.background = { color: COLORS.white };
    addContentHeader(sigSlide, 'Detected Signals', data.companyLogoUrl);
    addSlideFooter(sigSlide, dateStr);

    let sigY = 1.2;
    for (const signal of data.detected_signals) {
      sigSlide.addText(`${bulletChar}  ${signal}`, {
        x: MARGIN + 0.2, y: sigY, w: CONTENT_W - 0.4, h: 0.4,
        fontSize: 14, color: COLORS.text, valign: 'top', fontFace: 'Arial',
      });
      sigY += 0.45;
      if (sigY > SLIDE_H - 0.8) break;
    }
  }

  // ==========================================================================
  // SLIDE 4: Root Cause Analysis + Forecast
  // ==========================================================================
  const hasRootCause = data.root_cause_analysis && data.root_cause_analysis.length > 0;
  const hasForecast = data.forecast && (data.forecast['7_day'] || data.forecast['14_day'] || data.forecast['30_day']);

  if (hasRootCause || hasForecast) {
    const analysisSlide = pptx.addSlide();
    analysisSlide.background = { color: COLORS.white };
    addContentHeader(analysisSlide, 'Analysis & Forecast', data.companyLogoUrl);
    addSlideFooter(analysisSlide, dateStr);

    let anaY = 1.2;

    if (hasRootCause && data.root_cause_analysis) {
      addSectionTitle(analysisSlide, 'ROOT CAUSE ANALYSIS', anaY);
      anaY += 0.5;

      const warningChar = '\u26A0';
      for (const cause of data.root_cause_analysis) {
        analysisSlide.addText(`${warningChar}  ${cause}`, {
          x: MARGIN + 0.2, y: anaY, w: CONTENT_W - 0.4, h: 0.4,
          fontSize: 13, color: COLORS.text, valign: 'top', fontFace: 'Arial',
        });
        anaY += 0.45;
      }
      anaY += 0.3;
    }

    if (hasForecast && data.forecast) {
      addSectionTitle(analysisSlide, 'FORECAST PROJECTIONS', anaY);
      anaY += 0.55;

      const forecasts: { label: string; text: string; color: string }[] = [];
      if (data.forecast['7_day']) forecasts.push({ label: '7-Day Outlook', text: data.forecast['7_day'], color: COLORS.primary });
      if (data.forecast['14_day']) forecasts.push({ label: '14-Day Outlook', text: data.forecast['14_day'], color: COLORS.amber });
      if (data.forecast['30_day']) forecasts.push({ label: '30-Day Outlook', text: data.forecast['30_day'], color: COLORS.red });

      const fcCardW = (CONTENT_W - (forecasts.length - 1) * 0.25) / forecasts.length;
      forecasts.forEach((fc, idx) => {
        const cx = MARGIN + idx * (fcCardW + 0.25);
        const fcCardH = 2.0;
        analysisSlide.addShape('roundRect' as PptxGenJS.ShapeType, {
          x: cx, y: anaY, w: fcCardW, h: fcCardH,
          rectRadius: 0.1, fill: { color: COLORS.bgCard },
        });
        analysisSlide.addShape('rect' as PptxGenJS.ShapeType, {
          x: cx, y: anaY, w: fcCardW, h: 0.06,
          fill: { color: fc.color },
        });
        analysisSlide.addText(fc.label, {
          x: cx + 0.25, y: anaY + 0.2, w: fcCardW - 0.5, h: 0.3,
          fontSize: 13, bold: true, color: fc.color, fontFace: 'Arial',
        });
        analysisSlide.addText(fc.text, {
          x: cx + 0.25, y: anaY + 0.55, w: fcCardW - 0.5, h: 1.3,
          fontSize: 12, color: COLORS.text, valign: 'top', fontFace: 'Arial',
        });
      });
    }
  }

  // ==========================================================================
  // SLIDE 5: Accountability Table
  // ==========================================================================
  if (data.accountability && data.accountability.length > 0) {
    const acctSlide = pptx.addSlide();
    acctSlide.background = { color: COLORS.white };
    addContentHeader(acctSlide, 'Accountability', data.companyLogoUrl);
    addSlideFooter(acctSlide, dateStr);

    const headerRow: PptxGenJS.TableRow = [
      { text: 'ENTITY', options: { fontSize: 12, bold: true, color: COLORS.white, fill: { color: COLORS.dark }, valign: 'middle', fontFace: 'Arial' } },
      { text: 'OWNER', options: { fontSize: 12, bold: true, color: COLORS.white, fill: { color: COLORS.dark }, valign: 'middle', fontFace: 'Arial' } },
      { text: 'ISSUE', options: { fontSize: 12, bold: true, color: COLORS.white, fill: { color: COLORS.dark }, valign: 'middle', fontFace: 'Arial' } },
    ];

    const dataRows: PptxGenJS.TableRow[] = data.accountability.map((item, idx) => {
      const bgColor = idx % 2 === 0 ? COLORS.white : COLORS.bgLight;
      return [
        { text: item.entity, options: { fontSize: 12, color: COLORS.text, fill: { color: bgColor }, valign: 'middle', fontFace: 'Arial' } },
        { text: item.owner, options: { fontSize: 12, bold: true, color: COLORS.primary, fill: { color: bgColor }, valign: 'middle', fontFace: 'Arial' } },
        { text: item.issue, options: { fontSize: 12, color: COLORS.textLight, fill: { color: bgColor }, valign: 'middle', fontFace: 'Arial' } },
      ];
    });

    acctSlide.addTable([headerRow, ...dataRows], {
      x: MARGIN, y: 1.2, w: CONTENT_W,
      colW: [4, 3, 4.83],
      rowH: 0.5,
      border: { type: 'solid', pt: 0.5, color: COLORS.divider },
    });
  }

  // ==========================================================================
  // SLIDE 6: Recommended Actions
  // ==========================================================================
  if (data.recommended_actions && data.recommended_actions.length > 0) {
    const actionsSlide = pptx.addSlide();
    actionsSlide.background = { color: COLORS.white };
    addContentHeader(actionsSlide, 'Recommended Actions', data.companyLogoUrl);
    addSlideFooter(actionsSlide, dateStr);

    const firstAction = data.recommended_actions[0];
    const isStructured = typeof firstAction === 'object' && 'who' in (firstAction as Record<string, unknown>);

    if (isStructured) {
      const actHeaderRow: PptxGenJS.TableRow = [
        { text: 'WHO', options: { fontSize: 12, bold: true, color: COLORS.white, fill: { color: COLORS.dark }, valign: 'middle', fontFace: 'Arial' } },
        { text: 'WHAT', options: { fontSize: 12, bold: true, color: COLORS.white, fill: { color: COLORS.dark }, valign: 'middle', fontFace: 'Arial' } },
        { text: 'WHEN', options: { fontSize: 12, bold: true, color: COLORS.white, fill: { color: COLORS.dark }, valign: 'middle', fontFace: 'Arial' } },
      ];

      const actDataRows: PptxGenJS.TableRow[] = data.recommended_actions.map((rawAction, idx) => {
        const action = rawAction as unknown as PptxPrescriptiveAction;
        const bgColor = idx % 2 === 0 ? COLORS.white : COLORS.bgLight;
        return [
          { text: action.who || '', options: { fontSize: 12, bold: true, color: COLORS.primary, fill: { color: bgColor }, valign: 'middle', fontFace: 'Arial' } },
          { text: action.what || '', options: { fontSize: 12, color: COLORS.text, fill: { color: bgColor }, valign: 'middle', fontFace: 'Arial' } },
          { text: action.when || '', options: { fontSize: 12, bold: true, color: COLORS.amber, fill: { color: bgColor }, valign: 'middle', fontFace: 'Arial' } },
        ];
      });

      actionsSlide.addTable([actHeaderRow, ...actDataRows], {
        x: MARGIN, y: 1.2, w: CONTENT_W,
        colW: [3, 5.83, 3],
        rowH: 0.55,
        border: { type: 'solid', pt: 0.5, color: COLORS.divider },
      });
    } else {
      let actY = 1.2;
      for (let i = 0; i < data.recommended_actions.length; i++) {
        const action = typeof data.recommended_actions[i] === 'string' ? data.recommended_actions[i] : String(data.recommended_actions[i]);

        actionsSlide.addShape('ellipse' as PptxGenJS.ShapeType, {
          x: MARGIN + 0.1, y: actY + 0.05, w: 0.35, h: 0.35,
          fill: { color: COLORS.primary },
        });
        actionsSlide.addText(`${i + 1}`, {
          x: MARGIN + 0.1, y: actY + 0.05, w: 0.35, h: 0.35,
          fontSize: 12, bold: true, color: COLORS.white, align: 'center', valign: 'middle', fontFace: 'Arial',
        });
        actionsSlide.addText(action, {
          x: MARGIN + 0.6, y: actY, w: CONTENT_W - 0.8, h: 0.45,
          fontSize: 14, color: COLORS.text, valign: 'middle', fontFace: 'Arial',
        });
        actY += 0.55;
      }
    }
  }

  // ==========================================================================
  // SLIDE 7: Health Score Breakdown
  // ==========================================================================
  if (data.score_breakdown) {
    const bdSlide = pptx.addSlide();
    bdSlide.background = { color: COLORS.white };
    addContentHeader(bdSlide, 'Health Score Breakdown', data.companyLogoUrl);
    addSlideFooter(bdSlide, dateStr);

    const bd = data.score_breakdown;
    const CATEGORY_LABELS: Record<string, string> = {
      revenue: 'Revenue Impact',
      cash_flow: 'Cash Flow Impact',
      operations: 'Operations Impact',
      communication: 'Communication Impact',
      scheduling: 'Scheduling Impact',
    };

    const rows: PptxGenJS.TableRow[] = [];

    rows.push([
      { text: 'COMPONENT', options: { fontSize: 12, bold: true, color: COLORS.white, fill: { color: COLORS.dark }, valign: 'middle', fontFace: 'Arial' } },
      { text: 'VALUE', options: { fontSize: 12, bold: true, color: COLORS.white, fill: { color: COLORS.dark }, valign: 'middle', align: 'right', fontFace: 'Arial' } },
    ]);

    rows.push([
      { text: 'Base Score', options: { fontSize: 13, color: COLORS.text, fill: { color: COLORS.white }, valign: 'middle', fontFace: 'Arial' } },
      { text: String(bd.base_score), options: { fontSize: 13, color: COLORS.text, fill: { color: COLORS.white }, valign: 'middle', align: 'right', fontFace: 'Arial' } },
    ]);

    for (const [cat, penalty] of Object.entries(bd.category_penalties_capped || {})) {
      const label = CATEGORY_LABELS[cat] || cat.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
      rows.push([
        { text: label, options: { fontSize: 13, color: COLORS.text, fill: { color: COLORS.bgLight }, valign: 'middle', fontFace: 'Arial' } },
        { text: penalty > 0 ? `-${penalty}` : '0', options: { fontSize: 13, bold: penalty > 0, color: penalty > 0 ? COLORS.red : COLORS.textLight, fill: { color: COLORS.bgLight }, valign: 'middle', align: 'right', fontFace: 'Arial' } },
      ]);
    }

    if (bd.cross_system_penalty > 0) {
      rows.push([
        { text: 'Correlation Penalty', options: { fontSize: 13, color: COLORS.text, fill: { color: COLORS.white }, valign: 'middle', fontFace: 'Arial' } },
        { text: `-${bd.cross_system_penalty}`, options: { fontSize: 13, bold: true, color: COLORS.red, fill: { color: COLORS.white }, valign: 'middle', align: 'right', fontFace: 'Arial' } },
      ]);
    }

    if (bd.recovery_buffer > 0) {
      rows.push([
        { text: 'Recovery Offset', options: { fontSize: 13, color: COLORS.text, fill: { color: COLORS.bgLight }, valign: 'middle', fontFace: 'Arial' } },
        { text: `+${bd.recovery_buffer}`, options: { fontSize: 13, bold: true, color: COLORS.green, fill: { color: COLORS.bgLight }, valign: 'middle', align: 'right', fontFace: 'Arial' } },
      ]);
    }

    const finalColor = getHealthColor(bd.final_score);
    rows.push([
      { text: 'FINAL SCORE', options: { fontSize: 14, bold: true, color: COLORS.dark, fill: { color: COLORS.bgCard }, valign: 'middle', fontFace: 'Arial' } },
      { text: String(bd.final_score), options: { fontSize: 18, bold: true, color: finalColor, fill: { color: COLORS.bgCard }, valign: 'middle', align: 'right', fontFace: 'Arial' } },
    ]);

    bdSlide.addTable(rows, {
      x: 2, y: 1.4, w: 9,
      colW: [5.5, 3.5],
      rowH: 0.55,
      border: { type: 'solid', pt: 0.5, color: COLORS.divider },
    });
  }

  // ==========================================================================
  // Generate and download
  // ==========================================================================
  const safeTitle = data.title.replace(/[^a-zA-Z0-9 ]/g, '').substring(0, 40).trim().replace(/\s+/g, '_');
  const fileName = `Core314_Brief_${safeTitle}_${new Date(data.created_at).toISOString().slice(0, 10)}`;
  await pptx.writeFile({ fileName });
}
